import React, { useState, useEffect } from 'react';
import { 
  FileText, Trash2, Plus, RefreshCw, AlertTriangle, 
  Check, X, Loader2, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminContentPage = () => {
  const { getSettings, toggleIntegration, token } = useAdmin();
  const [settings, setSettings] = useState(null);
  const [demoStatus, setDemoStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsData, demoData] = await Promise.all([
        getSettings(),
        axios.get(`${API}/api/admin/demo-content/status`, { headers: getAuthHeaders() })
      ]);
      setSettings(settingsData);
      setDemoStatus(demoData.data);
    } catch (error) {
      toast.error('Failed to load content settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDemoContent = async () => {
    setToggling(true);
    try {
      await toggleIntegration('demo_content_enabled', !settings?.demo_content_enabled);
      toast.success(`Demo content ${!settings?.demo_content_enabled ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle demo content');
    } finally {
      setToggling(false);
    }
  };

  const handleGenerateDemo = async () => {
    if (!window.confirm('This will create sample demo listings. Continue?')) return;
    
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/admin/demo-content/generate`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(`Created ${res.data.created_count} demo listings`);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate demo listings');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteDemo = async () => {
    if (!window.confirm('This will permanently delete ALL demo listings. This action cannot be undone. Continue?')) return;
    
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/api/admin/demo-content/delete`, {
        headers: getAuthHeaders()
      });
      toast.success(`Deleted ${res.data.deleted_count} demo listings`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete demo listings');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-content">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Content / Listings Control
          </h1>
          <p className="text-slate-400">
            Manage demo content and placeholder listings
          </p>
        </div>
        <Button 
          variant="outline" 
          className="border-slate-600 text-slate-300"
          onClick={fetchData}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Demo Content Toggle */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              settings?.demo_content_enabled 
                ? 'bg-amber-500/20' 
                : 'bg-slate-700'
            }`}>
              {settings?.demo_content_enabled ? (
                <Eye className="w-7 h-7 text-amber-400" />
              ) : (
                <EyeOff className="w-7 h-7 text-slate-400" />
              )}
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold text-white">
                Demo Content Enabled
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                {settings?.demo_content_enabled 
                  ? 'Demo listings are visible to users (with DEMO badge)'
                  : 'Demo listings are hidden from users'
                }
              </p>
            </div>
          </div>
          <Switch
            checked={settings?.demo_content_enabled || false}
            onCheckedChange={handleToggleDemoContent}
            disabled={toggling}
            data-testid="toggle-demo-content"
          />
        </div>

        {settings?.demo_content_enabled && (
          <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-amber-400 mb-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Demo Mode Active</span>
            </div>
            <p className="text-slate-400 text-sm">
              Users will see demo listings with a visible "DEMO" badge. A banner will indicate 
              these are sample listings and not real inventory.
            </p>
          </div>
        )}
      </div>

      {/* Demo Listings Status */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl font-semibold text-white">
              Demo Listings
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              {demoStatus?.demo_listings_count || 0} demo listing(s) in database
            </p>
          </div>
          <Badge className={
            (demoStatus?.demo_listings_count || 0) > 0 
              ? 'bg-amber-500/20 text-amber-400'
              : 'bg-slate-700 text-slate-400'
          }>
            {(demoStatus?.demo_listings_count || 0) > 0 ? 'Has Demo Data' : 'No Demo Data'}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Generate Demo */}
          <div className="bg-slate-700/50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Generate Demo Listings</h3>
                <p className="text-xs text-slate-400">Create sample properties</p>
              </div>
            </div>
            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              onClick={handleGenerateDemo}
              disabled={generating}
              data-testid="generate-demo-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Generate Demo Listings
            </Button>
          </div>

          {/* Delete Demo */}
          <div className="bg-slate-700/50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Delete All Demo Listings</h3>
                <p className="text-xs text-slate-400">Remove all demo data</p>
              </div>
            </div>
            <Button
              variant="destructive"
              className="w-full"
              onClick={handleDeleteDemo}
              disabled={deleting || (demoStatus?.demo_listings_count || 0) === 0}
              data-testid="delete-demo-btn"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete All Demo Listings
            </Button>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
        <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-trust-blue" />
          How Demo Content Works
        </h2>
        
        <div className="space-y-4 text-slate-400 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-slate-700 rounded flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs text-white">1</span>
            </div>
            <p>
              <strong className="text-white">Demo Content OFF (Default):</strong> Only real property 
              listings are visible. Users see an empty state with a CTA to list their property if no 
              real listings exist.
            </p>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-slate-700 rounded flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs text-white">2</span>
            </div>
            <p>
              <strong className="text-white">Demo Content ON:</strong> Demo listings are visible alongside 
              real listings. All demo listings display a prominent "DEMO" badge, and a banner warns users 
              these are sample listings.
            </p>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-slate-700 rounded flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs text-white">3</span>
            </div>
            <p>
              <strong className="text-white">Managing Demo Data:</strong> Use "Generate Demo Listings" to 
              create sample properties for testing. Use "Delete All Demo Listings" to clean up when you're 
              ready to go live.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContentPage;
